import { makeApi } from "@zodios/core";
import { z } from "zod";

import {
  ChannelSchema,
  ChannelWithInformationSchema,
  EmailHealthMetricsSchema,
  EmailSendingUsageSchema,
  SenderDomainSchema,
  SenderProfileSchema,
  WccsChannelSchema,
  WebStreamConfigSchema,
  WhatsappConversationalComponentsSchema,
} from "../models";

export const TrackingEventTypeSchema = z.enum([
  "page_view",
  "add_to_cart",
  "purchase",
]);

const CreateChannelBaseRequestSchema = z.object({
  type: z.enum(["wccs", "whatsapp", "fb", "ig", "email"]),
});

const CreateEmailChannelSenderProfileSchema = z.object({
  name: z.string().min(1).max(255),
  fromEmail: z.string().min(1),
  replyTo: z.string().min(1),
});

/**
 * Body for adding a sender profile to an existing email channel. Same shape as
 * the profiles embedded in channel creation, but sent on its own.
 */
const CreateSenderProfileRequestSchema = CreateEmailChannelSenderProfileSchema;

/**
 * Body for updating a sender profile. Every field is optional (partial update)
 * and the backend rejects an all-empty body with `INVALID_REQUEST`.
 */
const UpdateSenderProfileRequestSchema =
  CreateEmailChannelSenderProfileSchema.partial();

const CreateEmailChannelRequestSchema = CreateChannelBaseRequestSchema.extend({
  type: z.literal("email"),
  /**
   * Sub domain — the channel's sender domain. Must be a strict subdomain
   * of `extra.brandDomain` (i.e. end with `.${brandDomain}`).
   */
  externalChannelId: z.string().min(1),
  extra: z.object({
    /**
     * Customer's brand domain. Used by the backend to derive the ESP
     * sub-user username; not stored on the Channel directly.
     */
    brandDomain: z.string().min(1),
    /**
     * 1-2 sender profiles. Each profile's `fromEmail` must use the same
     * domain as `externalChannelId` (sub domain).
     */
    senderProfiles: z
      .array(CreateEmailChannelSenderProfileSchema)
      .min(1)
      .max(2),
  }),
});

const CreateWccsChannelRequestSchema = CreateChannelBaseRequestSchema.extend({
  name: ChannelSchema.shape.name,
  type: z.literal("wccs"),
  externalChannelId: z.string().nullable(),
  externalChannelSecret: z.string().nullable(),
  externalChannelAccessToken: z.string().nullable(),
  /**
   * WCCS subtype. Omitted/absent means "web-chat" (the backend default —
   * see interlude PR #1218). Only "web-streams" changes behavior.
   */
  wccsType: z.enum(["web-chat", "web-streams"]).optional(),
});

const CreateWhatsappChannelRequestSchema =
  CreateChannelBaseRequestSchema.extend({
    type: z.literal("whatsapp"),
    externalChannelId: z.string(),
    extra: z.object({
      signupAuthCode: z.string(),
      wabaId: z.string(),
    }),
  });

const CreateFacebookChannelRequestSchema =
  CreateChannelBaseRequestSchema.extend({
    type: z.literal("fb"),
    externalChannelId: z.null(),
    externalChannelSecret: z.null(),
    externalChannelAccessToken: z.null(),
    extra: z.object({
      signupAuthCode: z.string(),
    }),
  });

const CreateInstgramChannelRequestSchema =
  CreateChannelBaseRequestSchema.extend({
    type: z.literal("ig"),
    externalChannelId: z.null(),
    externalChannelSecret: z.null(),
    externalChannelAccessToken: z.null(),
    extra: z.object({
      signupAuthCode: z.string(),
    }),
  });

const CreateChannelRequestSchema = z.discriminatedUnion("type", [
  CreateWccsChannelRequestSchema,
  CreateWhatsappChannelRequestSchema,
  CreateFacebookChannelRequestSchema,
  CreateInstgramChannelRequestSchema,
  CreateEmailChannelRequestSchema,
]);

const UpdateChannelBaseRequestSchema = z
  .object({
    name: ChannelSchema.shape.name,
    status: ChannelSchema.shape.status,
  })
  .partial();

const UpdateWccsChannelRequestSchema = UpdateChannelBaseRequestSchema.extend({
  externalChannelSecret:
    CreateWccsChannelRequestSchema.shape.externalChannelSecret,
  externalChannelAccessToken:
    CreateWccsChannelRequestSchema.shape.externalChannelAccessToken,
  channelInformation: WccsChannelSchema.shape.channelInformation
    .pick({
      webTrackingEnabled: true,
      webChatModuleEnabled: true,
      smartRedirectToolEnabled: true,
      webChatFooterDisabled: true,
      webChatConfig: true,
      subdomain: true,
    })
    .partial(),
}).partial();

/**
 * Spec: interlude PR #1216. Side-effect-free — never mutates state, only
 * checks. `available: false` means taken, not an error.
 */
export const SubdomainAvailabilityResponseSchema = z.object({
  available: z.boolean(),
});

const UpdateWhatsappChannelRequestSchema =
  UpdateChannelBaseRequestSchema.partial();

export const VerifyInstallationCheckSchema = z.object({
  id: z.string(),
  status: z.enum(["pass", "fail", "warning", "info", "skip"]),
  context: z.record(z.unknown()).nullable(),
});

export const VerifyInstallationResponseSchema = z.object({
  checks: z.array(VerifyInstallationCheckSchema),
  screenshot: z.string().nullable(),
  duration: z.number(),
  checkedAt: z.string(),
});

const api = makeApi([
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#2001f5d24ba14739b09f21f2be431d3c)
     */
    alias: "channels",
    method: "get",
    path: "/api/v1/channels/",
    response: z.object({
      channels: ChannelWithInformationSchema.array(),
    }),
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#bda29b988f164337b7c94dd976a9ccb8)
     */
    alias: "createChannel",
    method: "post",
    path: "/api/v1/channels/",
    parameters: [
      {
        type: "Body",
        name: "body",
        schema: CreateChannelRequestSchema,
      },
    ],
    response: ChannelWithInformationSchema,
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/202502-WhatsApp-admin-center-d24471c24b304e329fc656263c68b80a?pvs=4#1988ce15893880be8ba0caf4e2a5b89b)
     */
    alias: "reauthorizeChannel",
    method: "post",
    path: "/api/v1/channels/:channelId/authorize",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: z.object({
          type: z.enum(["whatsapp", "fb", "ig"]),
          extra: z.object({
            signupAuthCode: z.string(),
          }),
        }),
      },
    ],
    response: ChannelWithInformationSchema,
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#4a0dbcb4326544b9b3076f2cba1c206f)
     */
    alias: "getById",
    method: "get",
    path: "/api/v1/channels/:channelId",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: ChannelWithInformationSchema,
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#927752d3636843ee9061be8d89f23029)
     */
    alias: "updateChannel",
    method: "patch",
    path: "/api/v1/channels/:channelId",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: z.union([
          UpdateWccsChannelRequestSchema,
          UpdateWhatsappChannelRequestSchema,
        ]),
      },
    ],
    response: ChannelWithInformationSchema,
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#e2aea983e1a7423c8b4a080adebd9325)
     */
    alias: "addChannelDomain",
    method: "post",
    path: "/api/v1/channels/:channelId/domains",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: z.object({
          url: z.string(),
        }),
      },
    ],
    response: z.object({
      id: z.number().int().positive(),
      url: z.string(),
    }),
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#e12ee59f3ba44ed9a4ca3d6d7d5bdeb8)
     */
    alias: "updateChannelDomain",
    method: "put",
    path: "/api/v1/channels/:channelId/domains/:domainId",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Path",
        name: "domainId",
        schema: z.number().int(),
      },
      {
        type: "Body",
        name: "body",
        schema: z.object({
          url: z.string(),
        }),
      },
    ],
    response: z.unknown(),
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#4b8ced516d2a459994ffbb5560fb6b10)
     */
    alias: "deleteChannelDomain",
    method: "delete",
    path: "/api/v1/channels/:channelId/domains/:domainId",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Path",
        name: "domainId",
        schema: z.number().int(),
      },
    ],
    response: z.unknown(),
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/Web-SDK-P1-84374da2c0e5459dba58d84c18c06233?pvs=4#4a82385ed2d9472aaca0c0b6fa071cab)
     */
    alias: "getTrackingConnectionStatus",
    method: "get",
    path: "/api/v1/channels/:channelId/tracking-connection-status",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: z.object({
      status: ChannelSchema.shape.status,
    }),
  },
  {
    // TODO: add Notion spec link
    alias: "getEventStatus",
    method: "get",
    path: "/api/v1/channels/:channelId/event-status",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: z.object({
      events: z.array(
        z.object({
          eventType: TrackingEventTypeSchema,
          received: z.boolean(),
          lastReceivedAt: z.string().datetime().nullable(),
        }),
      ),
    }),
  },
  {
    /**
     * Spec: interlude `plans/whatsapp-conversational-components-api-contract.md`
     * Backend does not gate this endpoint (see contract's "Feature flag"
     * section) -- the frontend gates the UI entry point instead.
     */
    alias: "getConversationalComponents",
    method: "get",
    path: "/api/v1/channels/:channelId/conversational-components",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: WhatsappConversationalComponentsSchema,
  },
  {
    /**
     * Spec: interlude `plans/whatsapp-conversational-components-api-contract.md`
     * Partial-update semantics on the backend -- Phase 1 only ever sends
     * `prompts`; `commands` / `enable_welcome_message` are preserved
     * server-side (backend re-reads Meta's current state before writing).
     */
    alias: "updateConversationalComponents",
    method: "patch",
    path: "/api/v1/channels/:channelId/conversational-components",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: WhatsappConversationalComponentsSchema,
      },
    ],
    response: WhatsappConversationalComponentsSchema,
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/202510-EDM-in-Journey-onboaring-admin-center-2ab8ce15893880e68b2fd23f314cde09?source=copy_link#2ab8ce158938805a9f23cddf63841130)
     */
    alias: "getChannelSenderDomains",
    method: "get",
    path: "/api/v1/channels/:channelId/sender-domains",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: z.array(SenderDomainSchema),
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/202510-EDM-in-Journey-onboaring-admin-center-2ab8ce15893880e68b2fd23f314cde09?source=copy_link#2ad8ce15893880568a62cd827bd53d06)
     */
    alias: "validateSenderDomain",
    method: "post",
    path: "/api/v1/channels/:channelId/sender-domains/:domainId/validate",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Path",
        name: "domainId",
        schema: SenderDomainSchema.shape.id,
      },
    ],
    response: SenderDomainSchema,
  },
  {
    /**
     * Spec: interlude PR #1043 — `GET /api/v1/channels/{channel_id}/email/sending-usage`
     * Returns today's email sending usage and daily capacity for an email channel.
     */
    alias: "getEmailSendingUsage",
    method: "get",
    path: "/api/v1/channels/:channelId/email/sending-usage",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: EmailSendingUsageSchema,
  },
  {
    /**
     * Spec: interlude PR #1060 — `GET /api/v1/channels/{channel_id}/email/health-metrics`
     * Returns email channel sending stats (health metrics) — bounce count, spam
     * count, total sent, and a sending speed derived from the channel status.
     */
    alias: "getEmailHealthMetrics",
    method: "get",
    path: "/api/v1/channels/:channelId/email/health-metrics",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: EmailHealthMetricsSchema,
  },
  {
    /**
     * Spec: [Notion](https://www.notion.so/cresclab/202510-EDM-in-Journey-onboaring-admin-center-2ab8ce15893880e68b2fd23f314cde09?source=copy_link#2ad8ce1589388093b889fe04affe01c3)
     */
    alias: "getChannelSenderProfiles",
    method: "get",
    path: "/api/v1/channels/:channelId/sender-profiles",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: z.array(SenderProfileSchema),
  },
  {
    /**
     * Spec: interlude `POST /api/v1/channels/{channel_id}/sender-profiles`.
     * The sender domain is resolved from the channel, so `fromEmail` must use
     * that domain and no domain id is passed.
     */
    alias: "createSenderProfile",
    method: "post",
    path: "/api/v1/channels/:channelId/sender-profiles",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: CreateSenderProfileRequestSchema,
      },
    ],
    response: SenderProfileSchema,
  },
  {
    /**
     * Spec: interlude
     * `PATCH /api/v1/channels/{channel_id}/sender-profiles/{profile_id}`.
     * Partial update — the sender domain is fixed, so a supplied `fromEmail`
     * must stay under it.
     */
    alias: "updateSenderProfile",
    method: "patch",
    path: "/api/v1/channels/:channelId/sender-profiles/:profileId",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Path",
        name: "profileId",
        schema: SenderProfileSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: UpdateSenderProfileRequestSchema,
      },
    ],
    response: SenderProfileSchema,
  },
  {
    alias: "verifyInstallation",
    method: "post",
    path: "/api/v1/channels/:channelId/verify-installation",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: z.object({
          url: z.string().url().max(2048),
        }),
      },
    ],
    response: VerifyInstallationResponseSchema,
  },
  {
    /**
     * Spec: interlude `interlude/plans/2026-07-20-in2-gcs-config-writer-design.md`
     * Opaque GCS proxy — no backend field validation. Returns the stored
     * config.json object, or `null` if the channel has none saved yet.
     * Response is strictly `WebStreamConfigSchema` — a stored object that
     * doesn't match (e.g. saved under an older shape) throws here rather
     * than degrading gracefully; accepted tradeoff for type safety, see the
     * "Decision (pre-flight review)" note above.
     */
    alias: "getWebStreamConfig",
    method: "get",
    path: "/api/v1/channels/:channelId/web-stream-config",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
    ],
    response: WebStreamConfigSchema.nullable(),
  },
  {
    /**
     * Spec: interlude `interlude/plans/2026-07-20-in2-gcs-config-writer-design.md`
     * Overwrites the channel's config.json on GCS. Request body is strictly
     * validated client-side (this is a save from a validated RHF form);
     * response body is `z.unknown()` since the backend returns an empty/
     * null 200 body and callers only care that the mutation resolved.
     */
    alias: "putWebStreamConfig",
    method: "put",
    path: "/api/v1/channels/:channelId/web-stream-config",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Body",
        name: "body",
        schema: WebStreamConfigSchema,
      },
    ],
    response: z.unknown(),
  },
  {
    /**
     * Spec: interlude PR #1216. Side-effect-free onBlur check — excludes
     * this channel's own current subdomain, so re-checking an unchanged
     * value never falsely reports "taken". Format (not just uniqueness) is
     * also validated server-side; a malformed value 400s as
     * `CHANNEL_SUBDOMAIN_INVALID` rather than resolving `available: false`.
     */
    alias: "checkSubdomainAvailability",
    method: "get",
    path: "/api/v1/channels/:channelId/subdomain-availability",
    parameters: [
      {
        type: "Path",
        name: "channelId",
        schema: ChannelSchema.shape.id,
      },
      {
        type: "Query",
        name: "subdomain",
        schema: z.string().min(1),
      },
    ],
    response: SubdomainAvailabilityResponseSchema,
  },
]);

export { api };
